/**
 * A layout's history: every saved version, newest first, with who saved it, when and what changed.
 * Any version can be looked at in the hall, and restored (as a new version; nothing is lost).
 */

import type { LogRow, VersionRow } from '@mawkeb/shared/api';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { layoutState } from '../state/layout';
import { dropDraft, saving, useSaving } from '../state/saving';
import { venue } from '../state/store';
import { Close } from './icons';
import { ago } from './time';

const ACTIONS: Record<string, string> = {
  made: 'Made the layout',
  copied: 'Made the layout as a copy',
  renamed: 'Renamed it',
  deleted: 'Deleted it',
  restored: 'Restored it',
  'link-made': 'Made a view-only link',
  'link-off': 'Turned off a view-only link',
  'reciters-shown': 'Let view-only links show reciters',
  'reciters-hidden': 'Hid reciters from view-only links',
};

export function HistoryPanel({ onClose, onRestored }: { onClose: () => void; onRestored: () => void }) {
  const row = useSaving((s) => s.row);
  const looking = useSaving((s) => s.looking);
  const [versions, setVersions] = useState<VersionRow[] | null>(null);
  const [log, setLog] = useState<LogRow[]>([]);
  const [tab, setTab] = useState<'versions' | 'all'>('versions');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const id = row?.id;
  const current = row?.version;

  useEffect(() => {
    if (!id) return;
    api.versions(id).then((r) => setVersions(r.versions), (e: ApiError) => setProblem(e.status === 'offline' ? 'There’s no connection. The history needs one.' : e.message));
    api.log(id).then((r) => setLog(r.log), () => {});
  }, [id, current]);

  if (!row) return null;

  const look = async (v: VersionRow) => {
    if (v.version === row.version) return saving().stopLooking();
    setBusy(v.version);
    try {
      const r = await api.version(row.id, v.version);
      saving().lookAt(v.version, r.data);
    } catch (e) {
      venue().toast((e as Error).message, { tone: 'bad' });
    } finally {
      setBusy(null);
    }
  };

  const restore = async (version: number) => {
    const unsaved = layoutState().layout !== saving().saved;
    if (unsaved && !confirm('Restoring replaces the changes you haven’t saved. Restore anyway?')) return;
    setBusy(version);
    try {
      await api.restoreVersion(row.id, version, row.version);
      await dropDraft(row.id);
      venue().toast(`Restored version ${version}. It’s saved as the newest version.`);
      onRestored();
    } catch (e) {
      const err = e as ApiError;
      venue().toast(err.status === 409 ? 'Someone saved since you opened this. Reload the layout, then restore.' : err.message, { tone: 'bad' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <aside className="panel side history-panel" aria-label="History">
      <header>
        <h2>History</h2>
        <button type="button" className="icon" aria-label="Close the history" onClick={() => (saving().stopLooking(), onClose())}>
          <Close />
        </button>
      </header>
      <div className="seg small tabs" role="group" aria-label="What to show">
        <button type="button" aria-pressed={tab === 'versions'} onClick={() => setTab('versions')}>
          Saved versions
        </button>
        <button type="button" aria-pressed={tab === 'all'} onClick={() => setTab('all')}>
          Everything
        </button>
      </div>
      {looking && (
        <div className="looking" role="status">
          <p>You’re looking at version {looking.version}. It can’t be changed.</p>
          <div>
            <button type="button" className="primary small" disabled={busy !== null} onClick={() => void restore(looking.version)}>
              Restore this version
            </button>
            <button type="button" className="secondary small" onClick={() => saving().stopLooking()}>
              Back to the newest
            </button>
          </div>
        </div>
      )}
      {problem && <p className="form-problem">{problem}</p>}
      {tab === 'versions' ? (
        <ol className="versions">
          {versions?.map((v) => {
            const isCurrent = v.version === row.version;
            const shown = looking ? looking.version === v.version : isCurrent;
            return (
              <li key={v.version} className={shown ? 'shown' : undefined}>
                <button type="button" className="version" onClick={() => void look(v)} aria-current={shown || undefined} disabled={busy !== null}>
                  <span className="version-what">{v.summary}</span>
                  <span className="version-who">
                    Version {v.version}
                    {isCurrent ? ' (the newest)' : ''}, saved by {v.savedBy.name} {ago(v.savedAt)}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      ) : (
        <ol className="versions">
          {log.map((l, i) => (
            <li key={i}>
              <div className="version static">
                <span className="version-what">{l.action === 'saved' || l.action === 'restored-version' ? `Saved version ${l.version}: ${l.detail}` : (ACTIONS[l.action] ?? l.action) + (l.action === 'renamed' && l.detail ? ` (${l.detail})` : '')}</span>
                <span className="version-who">
                  {l.who.name} {ago(l.at)}
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}
